import { makeAutoObservable } from 'mobx'
import { RootStore } from 'stores'
import { IFilter, Where } from 'types/common'
import {
  getLineBalanceById,
  getAllLineBalances,
  countLineBalances,
  createLineBalance,
  updateLineBalanceById,
  deleteLineBalanceById,
  searchLineBalances,
  restoreLineBalanceById,
  archiveMultipleLineBalances,
  restoreMultipleLineBalances,
  createWorkStation,
  deleteWorkStation,
  unlinkStepFromWorkStation,
  updateWorkStationById,
  linkStepsToWorkStation,
  unlinkStepsFromWorkStation,
  updateStepsPositionsInStation,
  moveStepBetweenStations,
  updateReviewSettings,
  ReviewSettings,
  addStepsToEndOfStation,
  addStepsWithPositions,
  lockLineBalance,
  extendLockLineBalance,
  releaseLockLineBalance,
  // Practice Plan CRUD APIs
  createPracticePeriod,
  updatePracticePeriod,
  deletePracticePeriod,
  createPracticeLane,
  updatePracticeLane,
  deletePracticeLane,
  createPracticeSegment,
  updatePracticeSegment,
  deletePracticeSegment,
  duplicatePracticeSegment,
  createPracticeStation,
  updatePracticeStation,
  deletePracticeStation,
  duplicatePracticeStation,
  duplicatePracticeLane,
  duplicatePracticePeriod,
  createFirstStationInPeriod,
  createFirstSegmentInPeriod,
  updateSegmentPositions
} from 'API/plan'
import { createWorkplanVariant, duplicateWorkplan } from 'API/plan'
import { getStationDetailById } from 'API/plan'
import {
  createPracticeRotation,
  updatePracticeRotation,
  updatePracticeRotationLanes,
  deletePracticeRotation
} from 'API/practice-rotation'
import {
  IDuplicatePlanResponse,
  IStationDetail,
  IPracticePeriod,
  IPracticeLane,
  IPracticeRotation,
  IPracticeSegment,
  IPracticeStation,
  IPracticeStep,
  IWorkPlanDetailWithStations,
  IWorkPlan,
  IWorkPlanWithRelations,
  IWorkStation,
  IWorkplanSearchFilterDto
} from 'interfaces/plan'
import { EStatus, EMovePosition } from 'constants/workplan'
import { handleEditLockErrorAndNotify } from 'utils/editLock'

class PlanStore {
  rootStore: RootStore

  // Current workplan
  workplan: IWorkPlanWithRelations = {} as IWorkPlanWithRelations

  // List of workplans
  workplans: IWorkPlanWithRelations[] = []

  // Selected workplan
  selectedWorkplanId: number = -1
  selectedWorkplan: IWorkPlanWithRelations = {} as IWorkPlanWithRelations

  // Workplan detail
  workplanDetail: IWorkPlanDetailWithStations = {} as IWorkPlanDetailWithStations

  // UI states
  openWorkplanDetailPanel: boolean = false
  openInformationModal: boolean = false
  openCommentModal: boolean = false
  isManageModeInPlansList: boolean = false
  isManageModeInDetail: boolean = false
  // Edit mode inside WorkPlan Detail
  isEditingInDetail: boolean = false

  // Loading states
  isFetchingList: boolean = false
  isFetchingTotalNumber: boolean = false
  isFetchingDetail: boolean = false
  isUpdatingReviewSettings: boolean = false
  isSilentOperation: boolean = false
  isCreatingVariant: boolean = false
  isDuplicatingPlan: boolean = false
  isDuplicating: boolean = false
  isDeletingPeriod: boolean = false
  isDeletingLane: boolean = false
  isDeletingSegment: boolean = false
  isDeletingStation: boolean = false

  // Drag & drop blocking states
  isDraggingStepBetweenStations: boolean = false
  isMovingStepsBetweenStations: boolean = false

  // Pagination and count
  numberOfWorkplans: number = 0
  timeFetchingList: number = new Date().getTime()

  // Stations of selected/detail workplan
  workStations: IWorkStation[] = []

  // Station detail for user station page
  stationDetail: IStationDetail | null = null

  // UI: currently selected station id for assigning steps
  selectedStationId: number | null = null

  // Global selection of process steps across multiple processes in Practice Plan Detail
  selectedProcessStepIds: Set<number> = new Set()

  // Cache step names for selected steps to avoid repeated getStepName calls
  selectedProcessSteps: Map<number, { name: string; collectionId?: number }> = new Map()

  loadingProcessStepIds: Set<number> = new Set()

  // Rotation lane-selection mode. Null = not selecting. When set, the lane list in
  // the given period shows checkboxes. `editingRotationId != null` means we are
  // editing an existing rotation's membership (PUT lanes); null means creating a
  // new rotation. A fresh object is reassigned on every mutation for MobX reactivity.
  rotationSelection: { periodId: number; laneIds: Set<number>; editingRotationId: number | null } | null = null

  constructor(rootStore: RootStore) {
    makeAutoObservable(this)
    this.rootStore = rootStore
  }

  // Computed getters
  public get isBlockingInteractions(): boolean {
    return this.isDraggingStepBetweenStations || this.isMovingStepsBetweenStations
  }

  // Setters
  public setSelectedWorkplanId(workplanId: number): void {
    this.selectedWorkplanId = workplanId
  }

  public setSelectedStationId(workStationId: number | null): void {
    this.selectedStationId = Number.isFinite(workStationId) ? Number(workStationId) : null
  }

  public toggleProcessStepSelection(stepId: number, stepName?: string, collectionId?: number): void {
    const numericId = Number(stepId)
    if (!Number.isFinite(numericId)) return
    const next = new Set(this.selectedProcessStepIds)
    const nextSteps = new Map(this.selectedProcessSteps)

    if (next.has(numericId)) {
      next.delete(numericId)
      nextSteps.delete(numericId)
    } else {
      next.add(numericId)
      if (stepName) {
        nextSteps.set(numericId, { name: stepName, collectionId })
      }
    }

    this.selectedProcessStepIds = next
    this.selectedProcessSteps = nextSteps
  }

  public isProcessStepSelected(stepId: number): boolean {
    return this.selectedProcessStepIds.has(Number(stepId))
  }

  public clearSelectedProcessSteps(): void {
    this.selectedProcessStepIds = new Set()
    this.selectedProcessSteps = new Map()
  }

  public addLoadingProcessSteps(stepIds: number[]): void {
    const next = new Set(this.loadingProcessStepIds)
    stepIds.forEach(id => next.add(Number(id)))
    this.loadingProcessStepIds = next
  }

  public removeLoadingProcessSteps(stepIds: number[]): void {
    const next = new Set(this.loadingProcessStepIds)
    stepIds.forEach(id => next.delete(Number(id)))
    this.loadingProcessStepIds = next
  }

  public isProcessStepLoading(stepId: number): boolean {
    return this.loadingProcessStepIds.has(Number(stepId))
  }

  public setSelectedWorkplan(workplan: IWorkPlanWithRelations): void {
    this.selectedWorkplan = workplan
  }

  public setOpenInformationModal(status: boolean): void {
    this.openInformationModal = status
  }

  public setOpenCommentModal(status: boolean): void {
    this.openCommentModal = status
  }

  public setOpenWorkplanDetailPanel(status: boolean): void {
    this.openWorkplanDetailPanel = status
  }

  public setManageModeInPlansList(status: boolean): void {
    this.isManageModeInPlansList = status
  }

  public setManageModeInDetail(status: boolean): void {
    this.isManageModeInDetail = status
    if (!status) {
      this.isEditingInDetail = false
    }
  }

  public setEditingInDetail(status: boolean): void {
    // Only allow editing when Manage is on
    this.isEditingInDetail = this.isManageModeInDetail && !!status
  }

  public setSilentOperation(status: boolean): void {
    this.isSilentOperation = status
  }

  public setIsDraggingStepBetweenStations(status: boolean): void {
    this.isDraggingStepBetweenStations = status
  }

  public setIsMovingStepsBetweenStations(status: boolean): void {
    this.isMovingStepsBetweenStations = status
  }

  // API calls
  public async getWorkplanById(workplanId: number, filter: IFilter<IWorkPlan> = {}): Promise<void> {
    this.isFetchingDetail = true
    try {
      const workplan = await getLineBalanceById(workplanId, filter)
      this.workplan = workplan
      this.selectedWorkplan = workplan
      this.selectedWorkplanId = workplanId
    } finally {
      this.isFetchingDetail = false
    }
  }

  public async getAllWorkplans(filter: IFilter<IWorkPlan> = {}, orgId?: number): Promise<void> {
    const currentTime: number = new Date().getTime()
    if (currentTime > this.timeFetchingList) {
      this.timeFetchingList = currentTime
    }

    this.isFetchingList = true
    try {
      const responseData = await getAllLineBalances(filter, orgId)
      if (this.timeFetchingList === currentTime) {
        this.workplans = responseData
      }
    } finally {
      this.isFetchingList = false
    }
  }

  public async searchWorkplans(
    filterDto: IWorkplanSearchFilterDto,
    orgId?: number
  ): Promise<{ results: (IWorkPlanWithRelations & { periodCount: number })[]; totalCount: number }> {
    const currentTime: number = new Date().getTime()
    if (currentTime > this.timeFetchingList) {
      this.timeFetchingList = currentTime
    }

    this.isFetchingList = true
    try {
      const responseData = await searchLineBalances(filterDto, orgId)
      if (this.timeFetchingList === currentTime) {
        this.workplans = responseData.results
        this.numberOfWorkplans = responseData.totalCount
      }
      return responseData
    } finally {
      this.isFetchingList = false
    }
  }

  public async countWorkplans(where: Where<IWorkPlan> = {}): Promise<void> {
    this.isFetchingTotalNumber = true
    try {
      this.numberOfWorkplans = await countLineBalances(where)
    } finally {
      this.isFetchingTotalNumber = false
    }
  }

  public async createWorkplan(
    createDto: {
      name: string
      description?: string
      status?: number
    },
    orgId?: number
  ): Promise<IWorkPlan & { id: number }> {
    const newWorkplan = await createLineBalance(createDto, orgId)
    await this.getAllWorkplans({}, orgId)
    return newWorkplan
  }

  public async updateWorkplanById(id: number, updateBody: Partial<IWorkPlan>): Promise<void> {
    await updateLineBalanceById(id, updateBody)
    if (this.selectedWorkplanId === id) {
      await this.getWorkplanById(id)
    }
    await this.getAllWorkplans()
  }

  public async updateWorkplanStatus(id: number, status: EStatus): Promise<void> {
    try {
      await updateLineBalanceById(id, { status })
    } catch (error) {
      console.error('Failed to update workplan status:', error)
      throw error
    }
  }

  public async updateReviewSettings(id: number, settings: ReviewSettings): Promise<void> {
    try {
      this.isUpdatingReviewSettings = true
      await updateReviewSettings(id, settings)
      if (this.workplanDetail._id === id) {
        await this.getWorkplanDetailById(id)
      }
    } catch (error) {
      console.error('Failed to update review settings:', error)
      throw error
    } finally {
      this.isUpdatingReviewSettings = false
    }
  }

  public async deleteWorkplanById(id: number): Promise<void> {
    await deleteLineBalanceById(id)
    this.workplans = this.workplans.filter(wp => wp._id !== id)
    this.numberOfWorkplans = Math.max(0, this.numberOfWorkplans - 1)
    if (this.selectedWorkplanId === id) {
      this.selectedWorkplanId = -1
      this.selectedWorkplan = {} as IWorkPlanWithRelations
    }
  }

  public async archiveMultipleWorkplans(ids: number[]): Promise<void> {
    await archiveMultipleLineBalances(ids)
    this.workplans = this.workplans.filter(wp => !ids.includes(wp._id))
    this.numberOfWorkplans = Math.max(0, this.numberOfWorkplans - ids.length)
    if (ids.includes(this.selectedWorkplanId)) {
      this.selectedWorkplanId = -1
      this.selectedWorkplan = {} as IWorkPlanWithRelations
    }
  }

  public async restoreWorkplanById(id: number): Promise<void> {
    await restoreLineBalanceById(id)
    await this.getAllWorkplans()
  }

  public async restoreMultipleWorkplans(ids: number[]): Promise<void> {
    await restoreMultipleLineBalances(ids)
    await this.getAllWorkplans()
  }

  public async createVariant(originId: number, name?: string): Promise<IDuplicatePlanResponse> {
    this.isCreatingVariant = true
    try {
      const response = await createWorkplanVariant(originId, name)
      await this.getAllWorkplans()
      return response
    } finally {
      this.isCreatingVariant = false
    }
  }

  public async duplicate(lineBalanceId: number, name?: string): Promise<IDuplicatePlanResponse> {
    this.isDuplicatingPlan = true
    try {
      const response = await duplicateWorkplan(lineBalanceId, name)
      await this.getAllWorkplans()
      return response
    } finally {
      this.isDuplicatingPlan = false
    }
  }

  public resetWorkplanList(): void {
    this.workplans = []
    this.numberOfWorkplans = 0
  }

  public resetWorkplanDetail(): void {
    this.workplan = {} as IWorkPlanWithRelations
    this.workplanDetail = {} as IWorkPlanDetailWithStations
    // Drop any in-progress rotation lane-selection so it can't leak across plans
    // or onto the light/training builder (which shares HierarchyTabs).
    this.rotationSelection = null
  }

  public getWorkplanByIdFromStore(id: number): IWorkPlanWithRelations | undefined {
    return this.workplans.find(wp => wp._id === id)
  }

  public async getWorkplanDetailById(
    workplanId: number,
    options?: {
      includeAssignees?: boolean
      practiceEventId?: number
      includeStepContent?: boolean
      /** Org that owns the plan; pass the event's own org on schedule reads. */
      orgId?: number
    }
  ): Promise<IWorkPlanDetailWithStations> {
    // Only show global loading if not a silent operation
    if (!this.isSilentOperation) {
      this.isFetchingDetail = true
    }

    try {
      // Auto-detect practiceEventId from scheduleStore if not provided
      let practiceEventId = options?.practiceEventId
      let includeAssignees = options?.includeAssignees
      let includeStepContent = options?.includeStepContent

      // Auto-enable includeAssignees if currentEvent exists and not explicitly disabled
      if (includeAssignees === undefined && this.rootStore?.scheduleStore?.currentEvent?.id) {
        includeAssignees = true
      }

      if (includeAssignees && !practiceEventId && this.rootStore?.scheduleStore?.currentEvent?.id) {
        practiceEventId = this.rootStore.scheduleStore.currentEvent.id
      }

      // Practice Event context: if includeAssignees is true, automatically include step content
      // Practice Plan context (default): no flags (no includeAssignees, no includeStepContent)
      if (includeAssignees && includeStepContent === undefined) {
        includeStepContent = true
      }

      const requestOptions: {
        includeAssignees?: boolean
        practiceEventId?: number
        includeStepContent?: boolean
        orgId?: number
      } = {}
      if (options?.orgId !== undefined) {
        requestOptions.orgId = options.orgId
      }

      if (includeAssignees) {
        requestOptions.includeAssignees = true
        if (practiceEventId) {
          requestOptions.practiceEventId = practiceEventId
        }
        // Automatically include step content when in practice event context (unless explicitly disabled)
        if (includeStepContent !== false) {
          requestOptions.includeStepContent = includeStepContent ?? true
        }
      } else if (includeStepContent !== undefined) {
        // Only set includeStepContent if explicitly provided and not in practice event context
        requestOptions.includeStepContent = includeStepContent
      }

      const workplanDetail = await getLineBalanceById(workplanId, {}, requestOptions)
      this.workplanDetail = workplanDetail
      this.workStations = [...(workplanDetail.workStations ?? [])]
      return workplanDetail
    } finally {
      if (!this.isSilentOperation) {
        this.isFetchingDetail = false
      }
    }
  }

  public async createWorkStation(workplanId: number): Promise<IWorkStation | null> {
    try {
      const station = await createWorkStation(workplanId)
      this.workStations = [...this.workStations, station]
      return station
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        await this.getWorkplanDetailById(Number(workplanId))
        return null
      }
      throw error
    }
  }

  public async deleteWorkStation(workStationId: number): Promise<boolean> {
    try {
      await deleteWorkStation(workStationId)
      if (this.selectedStationId === workStationId) {
        this.selectedStationId = null
      }
      return true
    } catch (error) {
      const workplanId = this.workplanDetail?._id ?? this.workplan?._id
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        await this.getWorkplanDetailById(Number(workplanId))
        return false
      }
      throw error
    }
  }

  public async updateWorkStation(id: number, updateBody: { name?: string; duration?: number }): Promise<boolean> {
    try {
      await updateWorkStationById(id, updateBody)
      return true
    } catch (error) {
      const workplanId = this.workplanDetail?._id ?? this.workplan?._id
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        await this.getWorkplanDetailById(Number(workplanId))
        return false
      }
      console.error('Failed to update work station', error)
      throw error
    }
  }

  public setStations(stations: IWorkStation[]): void {
    this.workStations = stations
  }

  public setStationDetail(detail: IStationDetail | null): void {
    this.stationDetail = detail
  }

  public async unlinkStepFromStation(stepId: number, workStationId: number): Promise<boolean> {
    const workplanId = this.workplanDetail?._id ?? this.workplan?._id
    try {
      await unlinkStepFromWorkStation(workStationId, stepId)
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        if (Number.isFinite(workplanId)) await this.getWorkplanDetailById(Number(workplanId))
        return false
      }
      throw error
    }

    this.isSilentOperation = true
    try {
      if (Number.isFinite(workplanId)) {
        await this.getWorkplanDetailById(Number(workplanId))
      }
    } finally {
      this.isSilentOperation = false
    }
    return true
  }

  /**
   * Refetch the plan tree WITHOUT the global spinner — used after in-builder
   * mutations (e.g. instantiating a library component) so the new node appears
   * in place instead of flashing the full-screen loading overlay.
   */
  public async reloadPlanSilently(): Promise<void> {
    const workplanId = this.workplanDetail?._id ?? this.workplan?._id
    if (!Number.isFinite(workplanId)) return
    this.isSilentOperation = true
    try {
      await this.getWorkplanDetailById(Number(workplanId))
    } finally {
      this.isSilentOperation = false
    }
  }

  // Decreasing counter for optimistic node ids — negative, so they never collide
  // with real (positive) ids and are trivial to spot / replace on reload.
  private optimisticIdSeq = -1

  public nextOptimisticId(): number {
    return this.optimisticIdSeq--
  }

  /**
   * Optimistically insert a cloned-component node into the plan tree so a
   * drag-drop shows INSTANTLY, before the backend clone finishes. The node
   * carries a temporary negative id; a subsequent reloadPlanSilently() swaps the
   * whole tree for authoritative data on success, or reverts it on failure.
   */
  public insertOptimisticNode(
    parentType: 'linebalance' | 'period' | 'lane' | 'block',
    parentId: number,
    position: number,
    node: IPracticePeriod | IPracticeLane | IPracticeSegment | IPracticeStation
  ): void {
    const periods = this.workplanDetail?.periods
    if (!periods) return

    const insertSorted = <T extends { position?: number }>(arr: T[], item: T): void => {
      arr.push(item)
      arr.sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    }

    if (parentType === 'linebalance') {
      insertSorted(periods, node as IPracticePeriod)
      return
    }
    if (parentType === 'period') {
      const period = periods.find(p => p._id === parentId)
      if (!period) return
      period.lanes = period.lanes ?? []
      insertSorted(period.lanes, node as IPracticeLane)
      return
    }
    if (parentType === 'lane') {
      for (const p of periods) {
        const lane = p.lanes?.find(l => l._id === parentId)
        if (lane) {
          lane.segments = lane.segments ?? []
          insertSorted(lane.segments, node as IPracticeSegment)
          return
        }
      }
      return
    }
    // parentType === 'block' (a segment holds stations)
    for (const p of periods) {
      for (const l of p.lanes ?? []) {
        const seg = l.segments?.find(s => s._id === parentId)
        if (seg) {
          seg.stations = seg.stations ?? []
          insertSorted(seg.stations, node as IPracticeStation)
          return
        }
      }
    }
  }

  /**
   * Optimistic insert for a dropped ROTATION component: a rotation is a group +
   * member lanes (not a single node), so it adds a temp rotation to the period
   * plus temp member lanes (all negative ids, `rotationId` linking them). The
   * lanes render as skeletons inside the pink group until reloadPlanSilently()
   * swaps in authoritative data (or reverts on failure).
   */
  public insertOptimisticRotation(
    periodId: number,
    position: number,
    name: string,
    laneNames: string[]
  ): void {
    const period = this.workplanDetail?.periods?.find(p => p._id === periodId)
    if (!period) return

    const rotationId = this.nextOptimisticId()
    period.rotations = period.rotations ?? []
    period.rotations.push({
      _id: rotationId,
      practicePeriodId: periodId,
      name,
      position
    } as IPracticeRotation)

    period.lanes = period.lanes ?? []
    // A rotation always has ≥2 lanes; when no preview was loaded, show 2 placeholders.
    const names = laneNames.length ? laneNames : ['Lane', 'Lane']
    names.forEach((laneName, i) => {
      period.lanes!.push({
        _id: this.nextOptimisticId(),
        practicePeriodId: periodId,
        name: laneName,
        rotationId,
        position: position + i
      } as IPracticeLane)
    })
  }

  public async fetchStationDetail(stationId: number): Promise<IStationDetail> {
    this.isFetchingDetail = true
    try {
      const detail = await getStationDetailById(stationId)
      this.stationDetail = detail
      return detail
    } finally {
      this.isFetchingDetail = false
    }
  }

  public async unlinkStepsFromStation(stepIds: number[], workStationId: number): Promise<boolean> {
    if (stepIds.length === 0) return true
    const workplanId = this.workplanDetail?._id ?? this.workplan?._id
    try {
      await unlinkStepsFromWorkStation(workStationId, stepIds)
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        if (Number.isFinite(workplanId)) await this.getWorkplanDetailById(Number(workplanId))
        return false
      }
      throw error
    }

    this.isSilentOperation = true
    try {
      if (Number.isFinite(workplanId)) {
        await this.getWorkplanDetailById(Number(workplanId))
      }
    } finally {
      this.isSilentOperation = false
    }
    return true
  }

  public async linkStepToStation(stepId: number, workStationId: number): Promise<boolean> {
    return this.linkStepsToStation([stepId], workStationId)
  }

  public async linkStepsToStation(stepIds: number[], workStationId: number): Promise<boolean> {
    if (stepIds.length === 0) return true

    this.isSilentOperation = true
    try {
      const stationIndex = this.workStations.findIndex(station => Number(station?._id) === Number(workStationId))

      if (stationIndex !== -1) {
        const station = this.workStations[stationIndex]
        const currentSteps = station.steps || []

        const allStationStepPositions = currentSteps.map((step, index) => ({
          id: String(step._id).startsWith('temp-') ? (step as { stepId: number }).stepId : Number(step._id),
          position: index + 1
        }))

        let response
        try {
          response = await linkStepsToWorkStation({
            workStationId,
            stepIds,
            allStationStepPositions,
            newStepIds: stepIds
          })
        } catch (error) {
          const workplanId = this.workplanDetail?._id ?? this.workplan?._id
          if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
            return false
          }
          throw error
        }

        if (response && response.stationId === workStationId) {
          this.workStations = this.workStations.map(station => {
            if (Number(station._id) === Number(workStationId)) {
              return {
                ...station,
                steps: response.steps
              }
            }
            return station
          })
        }
      } else {
        const workplanId = this.workplanDetail?._id ?? this.workplan?._id
        if (Number.isFinite(workplanId)) {
          await this.getWorkplanDetailById(Number(workplanId))
        }
      }
      return true
    } finally {
      this.isSilentOperation = false
    }
  }

  public async addStepsToEndOfStation(stepIds: number[], workStationId: number): Promise<boolean> {
    if (stepIds.length === 0) return true

    const workplanId = this.workplanDetail?._id ?? this.workplan?._id
    const isPracticePlan = !!this.workplanDetail?.periods?.length

    // For Practice Plan structure: use optimistic update
    if (isPracticePlan) {
      const found = this.findStationInPracticePlan(workStationId)
      if (!found) {
        // Station not found in Practice Plan, fallback to regular API
        try {
          await addStepsToEndOfStation({ workStationId, stepIds })
        } catch (error) {
          if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
            if (Number.isFinite(workplanId)) {
              await this.getWorkplanDetailById(Number(workplanId))
            }
            return false
          }
          throw error
        }
        if (Number.isFinite(workplanId)) {
          this.isSilentOperation = true
          try {
            await this.getWorkplanDetailById(Number(workplanId))
          } finally {
            this.isSilentOperation = false
          }
        }
        return true
      }

      const existingSteps = found.station.steps || []
      const maxPosition = existingSteps.length > 0 ? Math.max(...existingSteps.map(s => s.position || 0)) : 0

      // Create temporary step objects for optimistic update
      const timestamp = Date.now()
      const newSteps: IPracticeStep[] = stepIds.map((stepId, index) => {
        const stepInfo = this.selectedProcessSteps.get(stepId)
        return {
          _id: -1 * (timestamp + index), // Negative temp ID to distinguish from real IDs
          stepId,
          name: stepInfo?.name || `Drill ${stepId}`,
          position: maxPosition + index + 1
        }
      })

      // Merge and sort steps by position
      const mergedSteps = [...existingSteps, ...newSteps].sort((a, b) => (a.position || 0) - (b.position || 0))

      // Optimistic update: update UI immediately
      this.updateStationStepsInPracticePlan(workStationId, mergedSteps)

      // Prepare API payload with all step positions
      const stepPositions = mergedSteps.map(step => ({
        stepId: step.stepId || step._id,
        position: step.position || 0,
        isNew: step._id < 0 // Negative IDs are new steps
      }))

      // Call API
      try {
        await addStepsWithPositions({ workStationId, stepPositions })
        // Reconcile temp (negative) ids → real ids in the BACKGROUND. The
        // optimistic list already shows the drill, so returning now lets the
        // caller toast success immediately instead of waiting on the full
        // line-balances reload. Silent flag keeps the reload spinner-free.
        if (Number.isFinite(workplanId)) {
          this.isSilentOperation = true
          void this.getWorkplanDetailById(Number(workplanId)).finally(() => {
            this.isSilentOperation = false
          })
        }
        return true
      } catch (error) {
        // On error: refresh data from server (NO revert, just refresh)
        if (Number.isFinite(workplanId)) {
          await this.getWorkplanDetailById(Number(workplanId))
        }
        if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
          return false
        }
        throw error
      }
    }

    // For legacy workStations structure
    try {
      await addStepsToEndOfStation({
        workStationId,
        stepIds
      })
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        if (Number.isFinite(workplanId)) {
          // Fetch detail with loading (not silent)
          await this.getWorkplanDetailById(Number(workplanId))
        }
        return false
      }
      throw error
    }

    if (Number.isFinite(workplanId)) {
      this.isSilentOperation = true
      try {
        await this.getWorkplanDetailById(Number(workplanId))
      } finally {
        this.isSilentOperation = false
      }
    }
    return true
  }

  /**
   * Simple add steps to end of station - only calls API, no detail refresh.
   * Used by button add handlers (like + button in ProcessCollectionSearch).
   */
  public async addStepsToEndOfStationSimple(stepIds: number[], workStationId: number): Promise<boolean> {
    if (stepIds.length === 0) return true

    const workplanId = this.workplanDetail?._id ?? this.workplan?._id
    const isPracticePlan = !!this.workplanDetail?.periods?.length

    try {
      if (isPracticePlan) {
        // For Practice Plan: use addStepsWithPositions API
        const found = this.findStationInPracticePlan(workStationId)
        const existingSteps = found?.station.steps || []
        const maxPosition = existingSteps.length > 0 ? Math.max(...existingSteps.map(s => s.position || 0)) : 0

        // Create step positions for API
        const stepPositions = stepIds.map((stepId, index) => ({
          stepId,
          position: maxPosition + index + 1,
          isNew: true
        }))

        await addStepsWithPositions({ workStationId, stepPositions })
      } else {
        // For legacy: use regular addStepsToEndOfStation API
        await addStepsToEndOfStation({ workStationId, stepIds })
      }
      return true
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        return false
      }
      throw error
    }
  }

  public async updateStepPositions(
    workStationId: number,
    stepPositions: { id: number; position: number }[]
  ): Promise<boolean> {
    this.isSilentOperation = true
    const workplanId = this.workplanDetail?._id ?? this.workplan?._id
    const isPracticePlan = !!this.workplanDetail?.periods?.length

    // For Practice Plan structure: use optimistic update
    if (isPracticePlan) {
      const found = this.findStationInPracticePlan(workStationId)
      if (found) {
        const existingSteps = found.station.steps || []

        // Optimistic update: reorder steps based on new positions
        const updatedSteps = existingSteps
          .map(step => {
            const stepId = Number(step._id)
            const updatedPosition = stepPositions.find(sp => Number(sp.id) === stepId)
            if (updatedPosition) {
              return { ...step, position: updatedPosition.position }
            }
            return step
          })
          .sort((a, b) => (a.position || 0) - (b.position || 0))

        this.updateStationStepsInPracticePlan(workStationId, updatedSteps)

        try {
          await updateStepsPositionsInStation(workStationId, stepPositions)
          return true
        } catch (error) {
          // On error: refresh data from server (NO revert, just refresh)
          if (Number.isFinite(workplanId)) {
            this.isSilentOperation = false
            await this.getWorkplanDetailById(Number(workplanId))
          }
          if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
            return false
          }
          throw error
        } finally {
          this.isSilentOperation = false
        }
      }
    }

    // For legacy workStations structure
    const previousStations = this.workStations.map(station => ({
      ...station,
      steps: station.steps ? station.steps.map(step => ({ ...step })) : station.steps
    }))

    const stationIndex = this.workStations.findIndex(station => Number(station?._id) === Number(workStationId))

    if (stationIndex !== -1) {
      const station = this.workStations[stationIndex]
      const updatedSteps = (station.steps || [])
        .map(step => {
          const stepId = Number(step?._id)
          const updatedPosition = stepPositions.find(sp => Number(sp.id) === stepId)
          if (updatedPosition) {
            return { ...step, position: updatedPosition.position }
          }
          return step
        })
        .sort((a, b) => (a.position || 0) - (b.position || 0))

      this.workStations[stationIndex] = { ...station, steps: updatedSteps }
    }

    try {
      await updateStepsPositionsInStation(workStationId, stepPositions)
    } catch (error) {
      this.workStations = previousStations
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        if (Number.isFinite(workplanId)) {
          // Ensure loading indicator by disabling silent mode, then fetch
          this.isSilentOperation = false
          await this.getWorkplanDetailById(Number(workplanId))
        }
        return false
      }
      throw error
    } finally {
      this.isSilentOperation = false
    }
    return true
  }

  public async updateSegmentPositions(updates: { id: number; position: number }[]): Promise<boolean> {
    this.isSilentOperation = true

    const previousDetail = this.workplanDetail ? { ...this.workplanDetail } : null

    try {
      await updateSegmentPositions(updates)
    } catch (error) {
      if (previousDetail) {
        this.workplanDetail = previousDetail
      }
      const workplanId = this.workplanDetail?._id ?? this.workplan?._id
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        if (Number.isFinite(workplanId)) {
          this.isSilentOperation = false
          await this.getWorkplanDetailById(Number(workplanId))
        }
        return false
      }
      throw error
    } finally {
      this.isSilentOperation = false
    }
    return true
  }

  public async moveStepBetweenStations(payload: {
    stepIds: number[]
    fromStationId: number
    toStationId: number
    newStationStepPositions: { id: number; position: number }[]
  }): Promise<boolean> {
    this.isSilentOperation = true
    try {
      await moveStepBetweenStations(payload)
    } catch (error) {
      const workplanId = this.workplanDetail?._id ?? this.workplan?._id
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        if (Number.isFinite(workplanId)) {
          // Show loading and refetch detail to sync UI
          this.isSilentOperation = false
          await this.getWorkplanDetailById(Number(workplanId))
        }
        return false
      }
      throw error
    } finally {
      this.isSilentOperation = false
    }
    return true
  }

  public async createPeriod(lineBalanceId: number, name: string): Promise<IPracticePeriod | null> {
    const workplanId = lineBalanceId || this.workplanDetail?._id
    try {
      const periods = this.workplanDetail?.periods || []
      const maxPosition = periods.length > 0 ? Math.max(...periods.map(p => p.position || 0)) : 0
      const period = await createPracticePeriod({
        lineBalanceId: workplanId,
        name,
        position: maxPosition + 1
      })
      await this.refreshWorkplanDetail()
      return period
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        await this.getWorkplanDetailById(Number(workplanId))
        return null
      }
      throw error
    }
  }

  public async updatePeriod(id: number, data: Partial<IPracticePeriod>): Promise<boolean> {
    const workplanId = this.workplanDetail?._id
    try {
      await updatePracticePeriod(id, data)
      await this.refreshWorkplanDetail()
      return true
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        await this.getWorkplanDetailById(Number(workplanId))
        return false
      }
      throw error
    }
  }

  public async deletePeriod(id: number): Promise<boolean> {
    const workplanId = this.workplanDetail?._id
    this.isDeletingPeriod = true
    try {
      await deletePracticePeriod(id)
      await this.refreshWorkplanDetail()
      return true
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        await this.getWorkplanDetailById(Number(workplanId))
        return false
      }
      throw error
    } finally {
      this.isDeletingPeriod = false
    }
  }

  public async createLane(practicePeriodId: number, name: string): Promise<IPracticeLane | null> {
    const workplanId = this.workplanDetail?._id
    try {
      const period = this.workplanDetail?.periods?.find(p => p._id === practicePeriodId)
      const lanes = period?.lanes || []
      const maxPosition = lanes.length > 0 ? Math.max(...lanes.map(l => l.position || 0)) : 0

      const lane = await createPracticeLane({
        practicePeriodId,
        name,
        position: maxPosition + 1
      })
      await this.refreshWorkplanDetail()
      return lane
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        await this.getWorkplanDetailById(Number(workplanId))
        return null
      }
      throw error
    }
  }

  public async updateLane(id: number, data: Partial<IPracticeLane>): Promise<boolean> {
    const workplanId = this.workplanDetail?._id
    try {
      await updatePracticeLane(id, data)
      await this.refreshWorkplanDetail()
      return true
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        await this.getWorkplanDetailById(Number(workplanId))
        return false
      }
      throw error
    }
  }

  public async deleteLane(id: number): Promise<boolean> {
    const workplanId = this.workplanDetail?._id
    this.isDeletingLane = true
    try {
      await deletePracticeLane(id)
      await this.refreshWorkplanDetail()
      return true
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        await this.getWorkplanDetailById(Number(workplanId))
        return false
      }
      throw error
    } finally {
      this.isDeletingLane = false
    }
  }

  // ── Rotation selection mode (create new / edit membership) ──

  get isRotationSelectionActive(): boolean {
    return this.rotationSelection !== null
  }

  /** Enter selection mode to build a NEW rotation in the given period. */
  public beginRotationSelection(periodId: number): void {
    this.rotationSelection = { periodId, laneIds: new Set(), editingRotationId: null }
  }

  /** Enter selection mode to EDIT an existing rotation's membership (preselect current members). */
  public beginRotationLaneEdit(periodId: number, rotationId: number): void {
    const period = this.workplanDetail?.periods?.find(p => p._id === periodId)
    const memberIds = (period?.lanes || []).filter(l => l.rotationId === rotationId).map(l => l._id)
    this.rotationSelection = { periodId, laneIds: new Set(memberIds), editingRotationId: rotationId }
  }

  public toggleRotationLane(laneId: number): void {
    if (!this.rotationSelection) return
    const next = new Set(this.rotationSelection.laneIds)
    if (next.has(laneId)) next.delete(laneId)
    else next.add(laneId)
    this.rotationSelection = { ...this.rotationSelection, laneIds: next }
  }

  public cancelRotationSelection(): void {
    this.rotationSelection = null
  }

  /**
   * Confirm the current selection: create a new rotation, or replace an existing
   * rotation's membership when editing. Requires ≥2 lanes. Clears selection on success.
   */
  public async confirmRotationSelection(): Promise<boolean> {
    const selection = this.rotationSelection
    if (!selection) return false
    const laneIds = [...selection.laneIds]
    if (laneIds.length < 2) return false

    if (selection.editingRotationId != null) {
      const ok = await this.updateRotationLanes(selection.editingRotationId, laneIds)
      if (ok) this.rotationSelection = null
      return ok
    }
    const created = await this.createRotation(selection.periodId, laneIds)
    if (created) this.rotationSelection = null
    return !!created
  }

  // ── Rotation CRUD ──

  public async createRotation(
    periodId: number,
    laneIds: number[],
    name?: string,
    rotateEveryMinutes?: number
  ): Promise<IPracticeRotation | null> {
    const workplanId = this.workplanDetail?._id
    try {
      const period = this.workplanDetail?.periods?.find(p => p._id === periodId)
      const rotations = period?.rotations || []
      const maxPosition = rotations.length > 0 ? Math.max(...rotations.map(r => r.position || 0)) : 0
      const rotation = await createPracticeRotation({
        practicePeriodId: periodId,
        name: name || `Rotation ${rotations.length + 1}`,
        rotateEveryMinutes: rotateEveryMinutes ?? 15,
        laneIds,
        position: maxPosition + 1
      })
      await this.refreshWorkplanDetail()
      return rotation
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        await this.getWorkplanDetailById(Number(workplanId))
        return null
      }
      throw error
    }
  }

  public async updateRotation(
    id: number,
    data: { name?: string; rotateEveryMinutes?: number; note?: string }
  ): Promise<boolean> {
    const workplanId = this.workplanDetail?._id
    try {
      await updatePracticeRotation(id, data)
      await this.refreshWorkplanDetail()
      return true
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        await this.getWorkplanDetailById(Number(workplanId))
        return false
      }
      throw error
    }
  }

  public async updateRotationLanes(id: number, laneIds: number[]): Promise<boolean> {
    const workplanId = this.workplanDetail?._id
    try {
      await updatePracticeRotationLanes(id, laneIds)
      await this.refreshWorkplanDetail()
      return true
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        await this.getWorkplanDetailById(Number(workplanId))
        return false
      }
      throw error
    }
  }

  public async deleteRotation(id: number): Promise<boolean> {
    const workplanId = this.workplanDetail?._id
    try {
      await deletePracticeRotation(id)
      await this.refreshWorkplanDetail()
      return true
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        await this.getWorkplanDetailById(Number(workplanId))
        return false
      }
      throw error
    }
  }

  public async createSegment(practiceLaneId: number, name: string): Promise<IPracticeSegment | null> {
    const workplanId = this.workplanDetail?._id
    try {
      // Find lane to calculate position
      let segments: IPracticeSegment[] = []
      for (const period of this.workplanDetail?.periods || []) {
        const lane = period.lanes?.find(l => l._id === practiceLaneId)
        if (lane) {
          segments = lane.segments || []
          break
        }
      }
      const maxPosition = segments.length > 0 ? Math.max(...segments.map(s => s.position || 0)) : 0

      const segment = await createPracticeSegment({
        practiceLaneId,
        name,
        position: maxPosition + 1
      })
      await this.refreshWorkplanDetail()
      return segment
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        await this.getWorkplanDetailById(Number(workplanId))
        return null
      }
      throw error
    }
  }

  public async updateSegment(id: number, data: Partial<IPracticeSegment>): Promise<boolean> {
    const workplanId = this.workplanDetail?._id
    try {
      await updatePracticeSegment(id, data)
      await this.refreshWorkplanDetail()
      return true
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        await this.getWorkplanDetailById(Number(workplanId))
        return false
      }
      throw error
    }
  }

  public async deleteSegment(id: number): Promise<boolean> {
    const workplanId = this.workplanDetail?._id
    this.isDeletingSegment = true
    try {
      await deletePracticeSegment(id)
      await this.refreshWorkplanDetail()
      return true
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        await this.getWorkplanDetailById(Number(workplanId))
        return false
      }
      throw error
    } finally {
      this.isDeletingSegment = false
    }
  }

  public async createStation(practiceSegmentId: number, name: string): Promise<IPracticeStation | null> {
    const workplanId = this.workplanDetail?._id
    try {
      let stations: IPracticeStation[] = []
      for (const period of this.workplanDetail?.periods || []) {
        for (const lane of period.lanes || []) {
          const segment = lane.segments?.find(s => s._id === practiceSegmentId)
          if (segment) {
            stations = segment.stations || []
            break
          }
        }
      }
      const maxPosition = stations.length > 0 ? Math.max(...stations.map(s => s.position || 0)) : 0

      const station = await createPracticeStation({
        practiceSegmentId,
        name,
        position: maxPosition + 1
      })
      await this.refreshWorkplanDetail()
      return station
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        await this.getWorkplanDetailById(Number(workplanId))
        return null
      }
      throw error
    }
  }

  public async createFirstStation(periodId: number): Promise<IPracticeStation | null> {
    const workplanId = this.workplanDetail?._id
    try {
      const result = await createFirstStationInPeriod(periodId)
      await this.refreshWorkplanDetail()
      return result.station
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        await this.getWorkplanDetailById(Number(workplanId))
        return null
      }
      throw error
    }
  }

  public async createFirstSegment(periodId: number): Promise<IPracticeSegment | null> {
    const workplanId = this.workplanDetail?._id
    try {
      // Name the auto-created first block consistently with subsequent blocks
      // ("New Block"), otherwise the backend default labels it "New Segment".
      const result = await createFirstSegmentInPeriod(periodId, { segmentName: 'New Block' })
      await this.refreshWorkplanDetail()
      return result.segment
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        await this.getWorkplanDetailById(Number(workplanId))
        return null
      }
      throw error
    }
  }

  public async updateStation(id: number, data: Partial<IPracticeStation>): Promise<boolean> {
    const workplanId = this.workplanDetail?._id
    try {
      await updatePracticeStation(id, data)
      await this.refreshWorkplanDetail()
      return true
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        await this.getWorkplanDetailById(Number(workplanId))
        return false
      }
      throw error
    }
  }

  public async deleteStation(id: number): Promise<boolean> {
    const workplanId = this.workplanDetail?._id
    this.isDeletingStation = true
    try {
      await deletePracticeStation(id)
      if (this.selectedStationId === id) {
        this.selectedStationId = null
      }
      await this.refreshWorkplanDetail()
      return true
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        await this.getWorkplanDetailById(Number(workplanId))
        return false
      }
      throw error
    } finally {
      this.isDeletingStation = false
    }
  }

  // Public so a panel that writes outside the store's own actions (the inspector's
  // Training RX form) reconciles through this instead of poking isSilentOperation.
  public async refreshWorkplanDetail(): Promise<void> {
    const workplanId = this.workplanDetail?._id
    if (!Number.isFinite(workplanId)) return

    this.isSilentOperation = true
    try {
      await this.getWorkplanDetailById(Number(workplanId))
    } finally {
      this.isSilentOperation = false
    }
  }

  // Helper: Find station in Practice Plan structure
  public findStationInPracticePlan(stationId: number): {
    station: IPracticeStation
    periodIndex: number
    laneIndex: number
    segmentIndex: number
    stationIndex: number
  } | null {
    const periods = this.workplanDetail?.periods || []
    for (let pi = 0; pi < periods.length; pi++) {
      const period = periods[pi]
      const lanes = period.lanes || []
      for (let li = 0; li < lanes.length; li++) {
        const lane = lanes[li]
        const segments = lane.segments || []
        for (let si = 0; si < segments.length; si++) {
          const segment = segments[si]
          const stations = segment.stations || []
          for (let sti = 0; sti < stations.length; sti++) {
            if (stations[sti]._id === stationId) {
              return {
                station: stations[sti],
                periodIndex: pi,
                laneIndex: li,
                segmentIndex: si,
                stationIndex: sti
              }
            }
          }
        }
      }
    }
    return null
  }

  // Helper: Update station steps immutably in Practice Plan structure
  // Made public for optimistic updates from WorkPlanBuilder during drag & drop
  public updateStationStepsInPracticePlan(stationId: number, newSteps: IPracticeStep[]): void {
    const found = this.findStationInPracticePlan(stationId)
    if (!found) return

    const { periodIndex, laneIndex, segmentIndex, stationIndex } = found

    // Immutable update
    this.workplanDetail = {
      ...this.workplanDetail,
      periods: this.workplanDetail.periods?.map((period, pi) =>
        pi !== periodIndex
          ? period
          : {
              ...period,
              lanes: period.lanes?.map((lane, li) =>
                li !== laneIndex
                  ? lane
                  : {
                      ...lane,
                      segments: lane.segments?.map((segment, si) =>
                        si !== segmentIndex
                          ? segment
                          : {
                              ...segment,
                              stations: segment.stations?.map((station, sti) =>
                                sti !== stationIndex
                                  ? station
                                  : {
                                      ...station,
                                      steps: newSteps
                                    }
                              )
                            }
                      )
                    }
              )
            }
      )
    } as IWorkPlanDetailWithStations
  }

  public async moveStation(stationId: number, targetSegmentId: number, position: EMovePosition): Promise<boolean> {
    const workplanId = this.workplanDetail?._id
    try {
      // Find source station and segment
      const sourceSegment = this.workplanDetail?.periods
        ?.flatMap(p => p.lanes || [])
        ?.flatMap(l => l.segments || [])
        ?.find(s => (s.stations || []).some(st => st._id === stationId))

      const sourceStation = sourceSegment?.stations?.find(s => s._id === stationId)
      const sourceSegmentId = sourceSegment?._id

      // Find target segment
      const targetSegment = this.workplanDetail?.periods
        ?.flatMap(p => p.lanes || [])
        ?.flatMap(l => l.segments || [])
        ?.find(s => s._id === targetSegmentId)

      if (!targetSegment) {
        throw new Error('Target segment not found')
      }

      const isSameSegment = sourceSegmentId === targetSegmentId
      const targetStations = (targetSegment.stations || []).filter(s => s._id !== stationId)
      const sourceStationPosition = sourceStation?.position || 0
      const newPosition =
        position === EMovePosition.END
          ? targetStations.length > 0
            ? Math.max(...targetStations.map(s => s.position || 0)) + 1
            : 1
          : 1

      if (isSameSegment && position === EMovePosition.BEGINNING) {
        // Moving within same segment to beginning: need to reorder both before and after
        const beforeStations = targetStations.filter(s => (s.position || 0) < sourceStationPosition)
        const afterStations = targetStations.filter(s => (s.position || 0) > sourceStationPosition)

        // First update the moved station
        await updatePracticeStation(stationId, {
          practiceSegmentId: targetSegmentId,
          position: 1
        })
        // Then update stations before (increase) and after (decrease)
        await Promise.all([
          ...beforeStations.map(s =>
            updatePracticeStation(s._id, {
              position: (s.position || 0) + 1
            })
          ),
          ...afterStations.map(s =>
            updatePracticeStation(s._id, {
              position: (s.position || 0) - 1
            })
          )
        ])
      } else {
        // Reorder source segment (if moving from different segment, or same segment with END)
        if (sourceSegmentId && !isSameSegment) {
          const sourceStations = (sourceSegment.stations || []).filter(s => s._id !== stationId)

          // Reorder source stations: decrease position for stations after the moved one
          await Promise.all(
            sourceStations
              .filter(s => (s.position || 0) > sourceStationPosition)
              .map(s =>
                updatePracticeStation(s._id, {
                  position: (s.position || 0) - 1
                })
              )
          )
        }

        // Reorder target segment
        if (position === EMovePosition.BEGINNING && targetStations.length > 0) {
          // First update the moved station
          await updatePracticeStation(stationId, {
            practiceSegmentId: targetSegmentId,
            position: 1
          })
          // Then update all other stations to shift positions
          await Promise.all(
            targetStations.map(s =>
              updatePracticeStation(s._id, {
                position: (s.position || 0) + 1
              })
            )
          )
        } else {
          await updatePracticeStation(stationId, {
            practiceSegmentId: targetSegmentId,
            position: newPosition
          })
        }
      }
      await this.refreshWorkplanDetail()
      return true
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        await this.getWorkplanDetailById(Number(workplanId))
        return false
      }
      throw error
    }
  }

  public async moveSegment(segmentId: number, targetLaneId: number, position: EMovePosition): Promise<boolean> {
    const workplanId = this.workplanDetail?._id
    try {
      // Find source lane and segment
      const sourceLane = this.workplanDetail?.periods
        ?.flatMap(p => p.lanes || [])
        ?.find(l => (l.segments || []).some(s => s._id === segmentId))

      const sourceSegment = sourceLane?.segments?.find(s => s._id === segmentId)
      const sourceLaneId = sourceLane?._id

      // Find target lane
      const targetLane = this.workplanDetail?.periods?.flatMap(p => p.lanes || [])?.find(l => l._id === targetLaneId)

      if (!targetLane) {
        throw new Error('Target lane not found')
      }

      const isSameLane = sourceLaneId === targetLaneId
      const targetSegments = (targetLane.segments || []).filter(s => s._id !== segmentId)
      const sourceSegmentPosition = sourceSegment?.position || 0
      const newPosition =
        position === EMovePosition.END
          ? targetSegments.length > 0
            ? Math.max(...targetSegments.map(s => s.position || 0)) + 1
            : 1
          : 1

      if (isSameLane && position === EMovePosition.BEGINNING) {
        // Moving within same lane to beginning: need to reorder both before and after
        const beforeSegments = targetSegments.filter(s => (s.position || 0) < sourceSegmentPosition)
        const afterSegments = targetSegments.filter(s => (s.position || 0) > sourceSegmentPosition)

        const updates = [
          { id: segmentId, position: 1, practiceLaneId: targetLaneId },
          ...beforeSegments.map(s => ({
            id: s._id,
            position: (s.position || 0) + 1,
            practiceLaneId: targetLaneId
          })),
          ...afterSegments.map(s => ({
            id: s._id,
            position: (s.position || 0) - 1,
            practiceLaneId: targetLaneId
          }))
        ]
        await updateSegmentPositions(updates)
      } else {
        // Reorder source lane (if moving from different lane, or same lane with END)
        if (sourceLaneId && !isSameLane) {
          const sourceSegments = (sourceLane.segments || []).filter(s => s._id !== segmentId)

          // Reorder source segments: decrease position for segments after the moved one
          const sourceUpdates = sourceSegments
            .filter(s => (s.position || 0) > sourceSegmentPosition)
            .map(s => ({
              id: s._id,
              position: (s.position || 0) - 1,
              practiceLaneId: sourceLaneId
            }))

          if (sourceUpdates.length > 0) {
            await updateSegmentPositions(sourceUpdates)
          }
        }

        // Reorder target lane
        if (position === EMovePosition.BEGINNING && targetSegments.length > 0) {
          const updates = [
            { id: segmentId, position: 1, practiceLaneId: targetLaneId },
            ...targetSegments.map(s => ({
              id: s._id,
              position: (s.position || 0) + 1,
              practiceLaneId: targetLaneId
            }))
          ]
          await updateSegmentPositions(updates)
        } else {
          await updatePracticeSegment(segmentId, {
            practiceLaneId: targetLaneId,
            position: newPosition
          })
        }
      }
      await this.refreshWorkplanDetail()
      return true
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        await this.getWorkplanDetailById(Number(workplanId))
        return false
      }
      throw error
    }
  }

  public async moveLane(laneId: number, targetPeriodId: number, position: EMovePosition): Promise<boolean> {
    const workplanId = this.workplanDetail?._id
    try {
      // Find source period and lane
      const sourcePeriod = this.workplanDetail?.periods?.find(p => (p.lanes || []).some(l => l._id === laneId))

      const sourceLane = sourcePeriod?.lanes?.find(l => l._id === laneId)
      const sourcePeriodId = sourcePeriod?._id

      // Find target period
      const targetPeriod = this.workplanDetail?.periods?.find(p => p._id === targetPeriodId)

      if (!targetPeriod) {
        throw new Error('Target period not found')
      }

      const isSamePeriod = sourcePeriodId === targetPeriodId
      const targetLanes = (targetPeriod.lanes || []).filter(l => l._id !== laneId)
      const sourceLanePosition = sourceLane?.position || 0
      const newPosition =
        position === EMovePosition.END
          ? targetLanes.length > 0
            ? Math.max(...targetLanes.map(l => l.position || 0)) + 1
            : 1
          : 1

      if (isSamePeriod && position === EMovePosition.BEGINNING) {
        // Moving within same period to beginning: need to reorder both before and after
        const beforeLanes = targetLanes.filter(l => (l.position || 0) < sourceLanePosition)
        const afterLanes = targetLanes.filter(l => (l.position || 0) > sourceLanePosition)

        // First update the moved lane
        await updatePracticeLane(laneId, {
          practicePeriodId: targetPeriodId,
          position: 1
        })
        // Then update lanes before (increase) and after (decrease)
        await Promise.all([
          ...beforeLanes.map(l =>
            updatePracticeLane(l._id, {
              position: (l.position || 0) + 1
            })
          ),
          ...afterLanes.map(l =>
            updatePracticeLane(l._id, {
              position: (l.position || 0) - 1
            })
          )
        ])
      } else {
        // Reorder source period (if moving from different period, or same period with END)
        if (sourcePeriodId && !isSamePeriod) {
          const sourceLanes = (sourcePeriod.lanes || []).filter(l => l._id !== laneId)

          // Reorder source lanes: decrease position for lanes after the moved one
          await Promise.all(
            sourceLanes
              .filter(l => (l.position || 0) > sourceLanePosition)
              .map(l =>
                updatePracticeLane(l._id, {
                  position: (l.position || 0) - 1
                })
              )
          )
        }

        // Reorder target period
        if (position === EMovePosition.BEGINNING && targetLanes.length > 0) {
          // First update the moved lane
          await updatePracticeLane(laneId, {
            practicePeriodId: targetPeriodId,
            position: 1
          })
          // Then update all other lanes to shift positions
          await Promise.all(
            targetLanes.map(l =>
              updatePracticeLane(l._id, {
                position: (l.position || 0) + 1
              })
            )
          )
        } else {
          await updatePracticeLane(laneId, {
            practicePeriodId: targetPeriodId,
            position: newPosition
          })
        }
      }
      await this.refreshWorkplanDetail()
      return true
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        await this.getWorkplanDetailById(Number(workplanId))
        return false
      }
      throw error
    }
  }

  public async duplicateSegment(segmentId: number, targetLaneId: number, position: EMovePosition): Promise<boolean> {
    const workplanId = this.workplanDetail?._id
    try {
      await duplicatePracticeSegment(segmentId, targetLaneId, position)
      await this.refreshWorkplanDetail()
      return true
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        await this.getWorkplanDetailById(Number(workplanId))
        return false
      }
      throw error
    }
  }

  public async duplicateStation(stationId: number, targetSegmentId: number, position: EMovePosition): Promise<boolean> {
    const workplanId = this.workplanDetail?._id
    try {
      await duplicatePracticeStation(stationId, targetSegmentId, position)
      await this.refreshWorkplanDetail()
      return true
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        await this.getWorkplanDetailById(Number(workplanId))
        return false
      }
      throw error
    }
  }

  public async duplicateLane(laneId: number, targetPeriodId: number, position: EMovePosition): Promise<boolean> {
    const workplanId = this.workplanDetail?._id
    try {
      await duplicatePracticeLane(laneId, targetPeriodId, position)
      await this.refreshWorkplanDetail()
      return true
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        await this.getWorkplanDetailById(Number(workplanId))
        return false
      }
      throw error
    }
  }

  public async duplicatePeriod(periodId: number, lineBalanceId: number, position: EMovePosition): Promise<boolean> {
    const workplanId = this.workplanDetail?._id
    this.isDuplicating = true
    try {
      await duplicatePracticePeriod(periodId, lineBalanceId, position)
      await this.refreshWorkplanDetail()
      return true
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        await this.getWorkplanDetailById(Number(workplanId))
        return false
      }
      throw error
    } finally {
      this.isDuplicating = false
    }
  }
}

export default PlanStore
